import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Paper,
  Switch,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Flag as FlagIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Build as BuildIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  BusinessCenter as BusinessCenterIcon,
} from '@mui/icons-material';
import { useFeatureFlags, FeatureFlags } from '../contexts/FeatureFlagsContext';
import { featureFlagsService } from '../services/featureFlagsService';
import { useNotify } from 'react-admin';

const flagCategories = {
  CRM: {
    icon: <BusinessIcon />,
    color: '#1976d2',
    flags: ['leads', 'opportunities', 'customers'],
    description: 'Customer relationship management features',
    path: 'crm',
  },
  Sales: {
    icon: <AssessmentIcon />,
    color: '#388e3c',
    flags: ['estimates', 'invoices', 'payments'],
    description: 'Sales and billing functionality',
    path: 'sales',
  },
  Operations: {
    icon: <BuildIcon />,
    color: '#f57c00',
    flags: ['calendar', 'routes'],
    description: 'Operational management tools',
    path: 'operations',
  },
  Assets: {
    icon: <BusinessCenterIcon />,
    color: '#7b1fa2',
    flags: ['vehicles', 'drivers'],
    description: 'Asset and resource management',
    path: 'assets',
  },
  Configuration: {
    icon: <SecurityIcon />,
    color: '#616161',
    flags: ['settings'],
    description: 'System configuration and administration',
    path: 'configuration',
  },
};

const flagDescriptions: Record<string, string> = {
  'crm.leads': 'Manage potential customers and prospects',
  'crm.opportunities': 'Track sales opportunities and deals',
  'crm.customers': 'Manage existing customer relationships',
  'sales.estimates': 'Create and manage project estimates',
  'sales.invoices': 'Handle billing and invoicing',
  'sales.payments': 'Track and manage payments',
  'operations.calendar': 'Schedule and manage appointments',
  'operations.routes': 'Plan and optimize delivery routes',
  'assets.vehicles': 'Manage vehicles and equipment',
  'assets.drivers': 'Manage driver information and assignments',
  'configuration.settings': 'System configuration and settings',
};

// Helper function to get nested flag value
const getNestedFlag = (flags: FeatureFlags, category: string, flag: string): boolean => {
  const categoryObj = flags[category as keyof FeatureFlags] as any;
  return categoryObj?.[flag] ?? false;
};

// Helper function to set nested flag value
const setNestedFlag = (flags: any, category: string, flag: string, value: boolean) => {
  return {
    ...flags,
    [category]: {
      ...flags[category],
      [flag]: value,
    },
  };
};

interface FeatureFlagCardProps {
  category: string;
  flagName: string;
  enabled: boolean;
  onToggle: (category: string, flagName: string, enabled: boolean) => void;
  saving?: boolean;
}

const FeatureFlagCard: React.FC<FeatureFlagCardProps> = ({
  category,
  flagName,
  enabled,
  onToggle,
  saving,
}) => {
  const flagPath = `${category}.${flagName}`;
  const displayName = flagName.charAt(0).toUpperCase() + flagName.slice(1);
  
  return (
    <Paper
      sx={{
        p: 2,
        border: enabled ? '2px solid #4caf50' : '2px solid #e0e0e0',
        backgroundColor: enabled ? '#f1f8e9' : '#fafafa',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {displayName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {flagDescriptions[flagPath] || `${displayName} functionality`}
          </Typography>
          <Chip
            label={enabled ? 'Enabled' : 'Disabled'}
            color={enabled ? 'success' : 'default'}
            size="small"
            icon={enabled ? <VisibilityIcon /> : <VisibilityOffIcon />}
          />
        </Box>
        <Box sx={{ ml: 2 }}>
          <Switch
            checked={enabled}
            onChange={e => onToggle(category, flagName, e.target.checked)}
            disabled={saving}
            color="primary"
            size="medium"
          />
        </Box>
      </Box>
    </Paper>
  );
};

export const FeatureFlagsSettings: React.FC = () => {
  const { flags, loading, error, refreshFlags } = useFeatureFlags();
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [localChanges, setLocalChanges] = useState<Partial<FeatureFlags>>({});
  const [expandedCategory, setExpandedCategory] = useState<string | false>('CRM');

  const hasUnsavedChanges = Object.keys(localChanges).length > 0;

  const handleToggleFlag = async (category: string, flagName: string, enabled: boolean) => {
    setLocalChanges(prev => setNestedFlag(prev, category, flagName, enabled));
  };

  const handleSave = async () => {
    if (!hasUnsavedChanges) {
      return;
    }

    try {
      setSaving(true);
      await featureFlagsService.updateFeatureFlags(localChanges);
      await refreshFlags();
      setLocalChanges({});
      notify(`Updated feature flags successfully`, { type: 'success' });
    } catch (err) {
      notify(
        `Failed to update feature flags: ${err instanceof Error ? err.message : 'Unknown error'}`,
        { type: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLocalChanges({});
  };

  const handleRefresh = async () => {
    try {
      await refreshFlags();
      setLocalChanges({});
      notify('Feature flags refreshed', { type: 'success' });
    } catch (err) {
      notify(
        `Failed to refresh feature flags: ${err instanceof Error ? err.message : 'Unknown error'}`,
        { type: 'error' }
      );
    }
  };

  const handleBulkEnable = async (category: string) => {
    const categoryConfig = flagCategories[category as keyof typeof flagCategories];
    let updates = { ...localChanges };
    categoryConfig.flags.forEach(flag => {
      updates = setNestedFlag(updates, categoryConfig.path, flag, true);
    });
    setLocalChanges(updates);
  };

  const handleBulkDisable = async (category: string) => {
    const categoryConfig = flagCategories[category as keyof typeof flagCategories];
    let updates = { ...localChanges };
    categoryConfig.flags.forEach(flag => {
      updates = setNestedFlag(updates, categoryConfig.path, flag, false);
    });
    setLocalChanges(updates);
  };

  const getEffectiveValue = (category: string, flagName: string) => {
    // Check if there's a local change first
    const localCategory = localChanges[category as keyof FeatureFlags] as any;
    if (localCategory && localCategory[flagName] !== undefined) {
      return localCategory[flagName];
    }
    // Fall back to current flags
    return getNestedFlag(flags, category, flagName);
  };

  const getEnabledCount = () => {
    let count = 0;
    Object.entries(flagCategories).forEach(([_, config]) => {
      config.flags.forEach(flag => {
        if (getEffectiveValue(config.path, flag)) {
          count++;
        }
      });
    });
    return count;
  };

  const getTotalCount = () => {
    return Object.values(flagCategories).reduce((total, config) => total + config.flags.length, 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        <Typography variant="h6" gutterBottom>
          Error Loading Feature Flags
        </Typography>
        <Typography>{error}</Typography>
        <Button onClick={handleRefresh} startIcon={<RefreshIcon />} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Feature Flags Management
            </Typography>
            <Stack direction="row" spacing={2}>
              {hasUnsavedChanges && (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={saving}
                    sx={{ minWidth: 120 }}
                  >
                    Reset Changes
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    startIcon={<SaveIcon />}
                    disabled={saving}
                    sx={{ minWidth: 120 }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                onClick={handleRefresh}
                startIcon={<RefreshIcon />}
                disabled={saving}
              >
                Refresh
              </Button>
            </Stack>
          </Box>

          {hasUnsavedChanges && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                You have unsaved changes. Don't forget to save your modifications.
              </Typography>
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Feature flags control which menu items and features are visible to users. Changes take
              effect immediately after saving. Disabled features will be hidden from the navigation
              menu.
            </Typography>
          </Alert>

          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 3 }}>
            <Tab label="Overview" value="overview" icon={<SettingsIcon />} />
            <Tab label="By Category" value="category" icon={<GroupIcon />} />
            <Tab label="All Features" value="all" icon={<FlagIcon />} />
          </Tabs>

          {activeTab === 'overview' && (
            <Box>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <AssessmentIcon />
                System Overview
              </Typography>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                    <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
                      {getEnabledCount()}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Enabled Features
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                    <Typography variant="h3" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      {getTotalCount()}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Total Features
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                {Object.entries(flagCategories).map(([category, config]) => {
                  const enabledInCategory = config.flags.filter(flag =>
                    getEffectiveValue(config.path, flag)
                  ).length;

                  return (
                    <Grid item xs={12} md={6} lg={4} key={category}>
                      <Paper
                        sx={{
                          p: 3,
                          border: `2px solid ${config.color}`,
                          borderRadius: 2,
                          height: '100%',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ color: config.color, mr: 1 }}>{config.icon}</Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {category}
                          </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {config.description}
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Chip
                            label={`${enabledInCategory}/${config.flags.length} enabled`}
                            color={enabledInCategory === config.flags.length ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>

                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleBulkEnable(category)}
                            disabled={saving}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Enable All
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleBulkDisable(category)}
                            disabled={saving}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Disable All
                          </Button>
                        </Stack>

                        <List dense>
                          {config.flags.map(flag => (
                            <ListItem key={flag} sx={{ px: 0 }}>
                              <ListItemText
                                primary={flag.charAt(0).toUpperCase() + flag.slice(1)}
                                secondary={flagDescriptions[`${config.path}.${flag}`]}
                              />
                              <ListItemSecondaryAction>
                                <Switch
                                  checked={getEffectiveValue(config.path, flag)}
                                  onChange={e =>
                                    handleToggleFlag(config.path, flag, e.target.checked)
                                  }
                                  disabled={saving}
                                  size="small"
                                />
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {activeTab === 'category' && (
            <Box>
              {Object.entries(flagCategories).map(([category, config]) => (
                <Accordion
                  key={category}
                  expanded={expandedCategory === category}
                  onChange={() =>
                    setExpandedCategory(expandedCategory === category ? false : category)
                  }
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ color: config.color, mr: 2 }}>{config.icon}</Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                        {category}
                      </Typography>
                      <Chip
                        label={`${config.flags.filter(flag =>
                          getEffectiveValue(config.path, flag)
                        ).length}/${config.flags.length} enabled`}
                        color={
                          config.flags.filter(flag => getEffectiveValue(config.path, flag))
                            .length === config.flags.length
                            ? 'success'
                            : 'default'
                        }
                        size="small"
                        sx={{ mr: 2 }}
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {config.description}
                    </Typography>

                    <Grid container spacing={2}>
                      {config.flags.map(flag => (
                        <Grid item xs={12} md={6} key={flag}>
                          <FeatureFlagCard
                            category={config.path}
                            flagName={flag}
                            enabled={getEffectiveValue(config.path, flag)}
                            onToggle={handleToggleFlag}
                            saving={saving}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {activeTab === 'all' && (
            <Box>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <FlagIcon />
                All Feature Flags
              </Typography>

              <Grid container spacing={2}>
                {Object.entries(flagCategories).map(([category, config]) =>
                  config.flags.map(flag => (
                    <Grid item xs={12} md={6} lg={4} key={`${config.path}.${flag}`}>
                      <FeatureFlagCard
                        category={config.path}
                        flagName={flag}
                        enabled={getEffectiveValue(config.path, flag)}
                        onToggle={handleToggleFlag}
                        saving={saving}
                      />
                    </Grid>
                  ))
                )}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
